import { ApolloClient } from "@apollo/client";
import jwt_decode from "jwt-decode";

interface AuthToken {
  exp: number;
  verified?: boolean;
  roles?: string[];
  profile_completion_status?: boolean;
  lastLogin?: string;
  username?: string;
  is_assesement_active?: boolean;
}

class AuthService {
  private TOKEN_KEY = "token";
  private ME_KEY = "me";
  private LOGIN_RETRY_COUNT = 0;
  private MAX_LOGIN_RETRIES = 2;

  // List of public operations that don't need authentication
  private PUBLIC_OPERATIONS = [
    "login_user",
    "loginUser",
    "logout_user",
    "logoutUser",
    "register_user",
    "registerUser",
    "reset_password_request",
    "resetPasswordRequest",
    "set_password",
    "setPassword",
    "verify_mobile",
    "verifyMobile",
    "resend_otp",
    "resendOtp",
    "check_user_existence",
    "checkUserExistence"
  ];

  // Clear authentication token and related data
  clearAuthToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.ME_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.ME_KEY);

    // Clear any other auth-related keys
    const authKeys = ["token", "jwt", "authToken", "access_token", "refresh_token", "me", "user"];
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  // Nuclear option - clear ALL site data
  async clearAllSiteData(): Promise<void> {
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();

    // Clear IndexedDB
    if (window.indexedDB && window.indexedDB.databases) {
      try {
        const databases = await window.indexedDB.databases();
        await Promise.all(
          databases.map(db => {
            if (db.name) {
              return window.indexedDB.deleteDatabase(db.name);
            }
            return Promise.resolve();
          })
        );
      } catch (e) {
        console.debug("Failed to clear IndexedDB:", e);
      }
    }

    // Clear cookies
    document.cookie.split(";").forEach(c => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Clear Apollo cache if exists
    const apolloClient = (window as any).__APOLLO_CLIENT__;
    if (apolloClient) {
      try {
        await apolloClient.clearStore();
        await apolloClient.resetStore();
      } catch (e) {
        console.debug("Failed to clear Apollo store:", e);
      }
    }

    // Clear any service worker cache
    if ("caches" in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
      } catch (e) {
        console.debug("Failed to clear service worker cache:", e);
      }
    }
  }

  // Set authentication token
  setAuthToken(token: string): void {
    if (token) {
      localStorage.setItem(this.TOKEN_KEY, token);
    }
  }

  // Set user data
  setUserData(userData: any): void {
    if (userData) {
      localStorage.setItem(this.ME_KEY, JSON.stringify(userData));
    }
  }

  // Get current token
  getAuthToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // Get user data
  getUserData(): any {
    const userData = localStorage.getItem(this.ME_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  // Check if token exists and is valid
  isTokenValid(): boolean {
    const token = this.getAuthToken();
    if (!token) return false;

    try {
      const decoded: AuthToken = jwt_decode(token);
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired
      if (decoded.exp && decoded.exp < currentTime) {
        return false;
      }
      
      return true;
    } catch (error) {
      console.debug("Invalid token format:", error);
      return false;
    }
  }

  // Get token payload
  getTokenPayload(): AuthToken | null {
    const token = this.getAuthToken();
    if (!token) return null;

    try {
      return jwt_decode<AuthToken>(token);
    } catch {
      return null;
    }
  }

  // Get user roles from token
  getUserRoles(): string[] {
    const payload = this.getTokenPayload();
    return payload?.roles || [];
  }

  // Check if operation is public (doesn't need auth)
  isPublicOperation(operationName?: string): boolean {
    if (!operationName) return false;
    
    return this.PUBLIC_OPERATIONS.some(op =>
      operationName.toLowerCase().includes(op.toLowerCase())
    );
  }

  // Handle authentication errors globally
  handleAuthError(error: any): boolean {
    const errorMessage = error?.message || error?.response?.data?.message || "";

    const authErrors = [
      "session has expired",
      "Session has expired",
      "Token has expired",
      "expired signature",
      "Signature has expired",
      "Authentication required",
      "Invalid authentication token",
      "authentication failed",
      "jwt expired",
      "JWT expired"
    ];

    if (authErrors.some(msg => errorMessage.toLowerCase().includes(msg.toLowerCase()))) {
      this.clearAuthToken();
      
      // Redirect to login with a delay to show message
      setTimeout(() => {
        const baseURL = process.env.REACT_APP_WORDPRESS_BASE_URI || "";
        window.location.replace(baseURL);
      }, 1000);

      return true;
    }

    return false;
  }

  // Logout user
  async logout(): Promise<void> {
    const token = this.getAuthToken(); // Get token before clearing

    // 1. Clear all local data first
    this.clearAuthToken();

    // 2. Clear Apollo Client cache
    const apolloClient = (window as any).__APOLLO_CLIENT__;
    if (apolloClient) {
      try {
        await apolloClient.clearStore();
      } catch (e) {
        console.debug("Failed to clear Apollo store:", e);
      }
    }

    // 3. Clear all session data
    sessionStorage.clear();

    // 4. Remove redirect URL if exists
    localStorage.removeItem("redirecturl");

    // 5. Redirect to WordPress base URL or login
    const baseURL = process.env.REACT_APP_WORDPRESS_BASE_URI || "";
    window.location.replace(baseURL);
  }

  // Reset retry count
  resetRetryCount(): void {
    this.LOGIN_RETRY_COUNT = 0;
  }

  // Increment and check retry count
  shouldRetry(): boolean {
    if (this.LOGIN_RETRY_COUNT < this.MAX_LOGIN_RETRIES) {
      this.LOGIN_RETRY_COUNT++;
      return true;
    }
    return false;
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    const roles = this.getUserRoles();
    return roles.includes(role);
  }

  // Get default route based on user role
  getDefaultRoute(): string {
    if (!this.isTokenValid()) {
      return "/";
    }

    const roles = this.getUserRoles();
    const userData = this.getUserData();

    if (roles.length === 0) {
      return "/";
    }

    const primaryRole = roles[0];

    switch (primaryRole) {
      case "college":
        return "/campus-dashboard";
      case "employer_user":
      case "sub_user":
        return "/employer-dashboard";
      case "student":
        return "/student-dashboard";
      case "super_admin":
        return "/admin-dashboard";
      default:
        return "/";
    }
  }
}

// Create singleton instance
const authService = new AuthService();

// Export as default
export default authService;

// Also export the class for testing purposes
export { AuthService };